/**
 * PatientPreferencesFields — Marital status · Preferred language · Interpreter
 * required.
 *
 * EXTRACTED from `DemographicsSection` (PROD-1988) so the chart's Demog tab can
 * render the SAME three controls it does. Flutter's chart Demog tab
 * (`demog_tab.dart:355-560` `rightPanel()`) shows exactly these three fields —
 * and only these — in its editable FormView, while the patient-edit
 * Demographics section shows them among the full demographic set. One
 * implementation, two hosts: a second pair of pickers would drift.
 *
 * Requires a `FormProvider<PersonalDetailsFormValues>` above it (both hosts
 * supply one). Writes with `shouldDirty` so either host's Save enables.
 */

import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMaritalStatuses, useLanguages } from '@/shared/services/lookups';
import { MasterTypeahead } from './master-typeahead';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { PillPicker } from './form-primitives';
import { FieldLabel, BrandSpinner } from '@scripsteam/scrips-signal-ds';
import type { PersonalDetailsFormValues } from './personal-details-schema';

export function PatientPreferencesFields() {
  const { control, setValue } = useFormContext<PersonalDetailsFormValues>();
  const maritalStatus = useWatch({ control, name: 'maritalStatus' });
  const interpreterRequired = useWatch({ control, name: 'interpreterRequired' });
  const languageName = useWatch({ control, name: 'languageName' });

  const maritalsQ = useMaritalStatuses();
  const [languageQuery, setLanguageQuery] = useState('');
  const debouncedLanguage = useDebounce(languageQuery, 300);
  const languagesQ = useLanguages(debouncedLanguage);

  return (
    <>
      {/* Marital status (pills) */}
      <div>
        <FieldLabel>Marital status</FieldLabel>
        <div className="mt-2">
          {maritalsQ.isPending ? (
            <BrandSpinner size={20} label="Loading…" />
          ) : (
            <PillPicker
              options={(maritalsQ.data ?? []).map((m) => ({
                value: m.id,
                label: m.name,
              }))}
              value={maritalStatus ?? null}
              onChange={(v) => {
                setValue('maritalStatus', v, { shouldDirty: true });
                const ms = maritalsQ.data?.find((m) => m.id === v);
                setValue(
                  'maritalStatusName',
                  ms ? { id: ms.id, name: ms.name } : null,
                  { shouldDirty: true },
                );
              }}
            />
          )}
        </div>
      </div>

      {/* Preferred language (search) */}
      <div>
        <FieldLabel>Preferred language</FieldLabel>
        <div className="mt-1">
          <MasterTypeahead
            value={languageName}
            onSelect={(item) => {
              setValue('language', item?.id ?? null, { shouldDirty: true });
              setValue('languageName', item, { shouldDirty: true });
            }}
            placeholder="Enter Language"
            query={languageQuery}
            onQueryChange={setLanguageQuery}
            results={languagesQ.data ?? []}
            isPending={languagesQ.isFetching}
          />
        </div>
      </div>

      {/* Interpreter required (Yes/No pills) */}
      <div>
        <FieldLabel>Interpreter required</FieldLabel>
        <div className="mt-2">
          <PillPicker
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
            value={
              interpreterRequired == null
                ? null
                : interpreterRequired
                  ? 'yes'
                  : 'no'
            }
            onChange={(v) =>
              setValue('interpreterRequired', v == null ? null : v === 'yes', {
                shouldDirty: true,
              })
            }
          />
        </div>
      </div>
    </>
  );
}
